import React, { useState, useEffect } from 'react';
import { Steps, Card, Button, Space, Modal, Progress } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setCurrentStep as setCurrentStepAction, markStepCompleted, resetNavigation } from '../../redux/reducers/navigationSlice';
import { purgeIncidentData } from '../../redux/store';
import { 
  CarOutlined, 
  UserOutlined, 
  CameraOutlined, 
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import VinScanStep from './steps/VinScanStep';
import VehicleConfirmationStep from './steps/VehicleConfirmationStep';
import OperatorSelectionStep from './steps/OperatorSelectionStep';
import PhotoCaptureStep from './steps/PhotoCaptureStep';
import DamageAssessmentStep from './steps/DamageAssessmentStep';
import IncidentDetailsStep from './steps/IncidentDetailsStep';
import ReviewSubmitStep from './steps/ReviewSubmitStep';
import './IncidentCreationScreen.css';

const { Step } = Steps;

const steps = [
  {
    title: 'Vehicle ID',
    icon: <CarOutlined />,
    component: VinScanStep,
  },
  {
    title: 'Confirm',
    icon: <CarOutlined />,
    component: VehicleConfirmationStep,
  },
  {
    title: 'Operator',
    icon: <UserOutlined />,
    component: OperatorSelectionStep,
  },
  {
    title: 'Photos',
    icon: <CameraOutlined />,
    component: PhotoCaptureStep,
  },
  {
    title: 'Damage',
    icon: <WarningOutlined />,
    component: DamageAssessmentStep,
  },
  {
    title: 'Details',
    icon: <FileTextOutlined />,
    component: IncidentDetailsStep,
  },
  {
    title: 'Review',
    icon: <CheckCircleOutlined />,
    component: ReviewSubmitStep,
  },
];

export const IncidentCreationScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Get current step from Redux state
  const reduxCurrentStep = useAppSelector(state => state.navigation.currentStep);
  const completedSteps = useAppSelector(state => state.navigation.completedSteps);
  
  const [currentStep, setCurrentStepLocal] = useState(reduxCurrentStep);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isStepsExpanded, setIsStepsExpanded] = useState(false);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(process.env.NODE_ENV === 'development');
  
  // Sync local state with Redux state
  useEffect(() => {
    setCurrentStepLocal(reduxCurrentStep);
  }, [reduxCurrentStep]);
  
  // Update function to sync with Redux
  const setCurrentStep = (step: number) => {
    setCurrentStepLocal(step);
    dispatch(setCurrentStepAction(step));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      dispatch(markStepCompleted(currentStep));
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAbandon = () => {
    Modal.confirm({
      title: 'Abandon Incident?',
      content: 'Are you sure you want to abandon this incident? All progress will be lost.',
      okText: 'Yes, Abandon',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => {
        dispatch(purgeIncidentData());
        dispatch(resetNavigation());
        // Reset individual slices since we don't have RESET_STATE action
        navigate('/home');
      },
    });
  };

  const handleComplete = () => {
    navigate('/home');
  };

  const handleSkipToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  const handleSkipForward = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkipBackward = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  // Combined Mobile Navigation Component
  const MobileCombinedNav = () => {
    const progressPercent = Math.round(((currentStep + 1) / steps.length) * 100);
    
    return (
      <div className="mobile-combined-nav">
        {/* Progress Bar Section */}
        <div className="mobile-nav-header">
          <div className="mobile-step-info">
            <span className="mobile-step-counter">Step {currentStep + 1} of {steps.length}: </span>
            <span className="mobile-step-title">{steps[currentStep].title}</span>
          </div>
          <Button
            type="text"
            size="small"
            icon={isStepsExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsStepsExpanded(!isStepsExpanded)}
            className="mobile-nav-dropdown"
          />
        </div>
        
        <Progress 
          percent={progressPercent} 
          showInfo={false}
          strokeColor="#FF9902"
          className="mobile-progress-bar"
        />
        
        {/* Expandable Steps List */}
        {isStepsExpanded && (
          <div className="mobile-steps-dropdown">
            <div className="mobile-steps-list">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className={`mobile-step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => {
                    handleSkipToStep(index);
                    setIsStepsExpanded(false);
                  }}
                >
                  <div className="mobile-step-icon">
                    {step.icon}
                  </div>
                  <div className="mobile-step-content">
                    <span className="mobile-step-name">{step.title}</span>
                    <span className="mobile-step-number">Step {index + 1}</span>
                  </div>
                  <div className="mobile-step-status">
                    {index < currentStep && <CheckCircleOutlined className="mobile-step-check" />}
                    {index === currentStep && <span className="mobile-step-current">Current</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Development Navigation Toolbar
  const DevelopmentToolbar = () => {
    if (!isDevelopmentMode) return null;

    return (
      <div className="dev-toolbar">
        <div className="dev-toolbar-header">
          <strong>🚧 DEV TOOLS</strong>
          <Button 
            type="link" 
            size="small" 
            onClick={() => setIsDevelopmentMode(!isDevelopmentMode)}
          >
            Hide
          </Button>
        </div>
        <div className="dev-toolbar-content">
          <Space wrap>
            <Button size="small" onClick={handleSkipBackward} disabled={currentStep === 0}>
              ← Previous
            </Button>
            <Button size="small" onClick={handleSkipForward} disabled={currentStep === steps.length - 1}>
              Next →
            </Button>
            <span className="dev-step-selector">Jump to:</span>
            {steps.map((step, index) => (
              <Button 
                key={index}
                size="small"
                type={index === currentStep ? 'primary' : 'default'}
                onClick={() => handleSkipToStep(index)}
              >
                {index + 1}
              </Button>
            ))}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <div className="incident-creation-container">
      <Card className="incident-creation-card">
        <div className="incident-header">
          <h1>Create Incident Report</h1>
          <Button danger onClick={handleAbandon}>
            Abandon Incident
          </Button>
        </div>

        {isMobile ? (
          <MobileCombinedNav />
        ) : (
          <Steps 
            current={currentStep} 
            className="incident-steps"
            direction="horizontal"
            size="default"
          >
            {steps.map((step, index) => (
              <Step key={index} title={step.title} icon={step.icon} />
            ))}
          </Steps>
        )}

        <DevelopmentToolbar />

        <div className="step-content">
          <CurrentStepComponent 
            onNext={handleNext}
            onPrevious={handlePrevious}
            onComplete={handleComplete}
            isFirstStep={currentStep === 0}
            isLastStep={currentStep === steps.length - 1}
          />
        </div>
      </Card>
    </div>
  );
};